/**
 * flattenChartForCopilot
 * ----------------------
 * Turns the chart's nested `teeth` map into a flat array shaped exactly for
 * Viyora Copilot prompts:
 *
 *   [
 *     { tooth: "16", surfaces: ["O"], condition: "caries",
 *       label: "Caries", category: "diagnosis" },
 *     ...
 *   ]
 *
 * — Skips teeth with no conditions.
 * — Expands multiple conditions on the same tooth into separate rows so the
 *   LLM can reason surface-by-surface.
 * — Optional `meta` argument accepts the CONDITION_META_V2 dictionary so
 *   `label` + `category` are attached without hard-coding it in this helper.
 */
export function flattenChartForCopilot(chart, meta = {}) {
  const teeth = (chart && chart.teeth) || {};
  const findings = [];
  for (const [tooth, entry] of Object.entries(teeth)) {
    const conditions = entry?.conditions || [];
    for (const c of conditions) {
      const isCustom = c.code === "custom";
      findings.push({
        tooth,
        surfaces: Array.isArray(c.surfaces) ? c.surfaces : [],
        condition: c.code,
        label: isCustom ? (c.label || "Custom") : (meta[c.code]?.label || c.code),
        category: isCustom ? "custom" : (meta[c.code]?.category || "other"),
        note: c.note || undefined,
        added_at: c.added_at || undefined,
      });
    }
  }
  return findings;
}

/**
 * copilotFindingsSummary — short human-readable one-liner useful as a chat
 * seed message ("I have 3 findings on this patient: 16-caries, 26-composite,
 * 46-implant. Suggest a plan.").
 */
export function copilotFindingsSummary(findings) {
  if (!findings.length) return "No findings on chart — all teeth healthy.";
  const grouped = findings.slice(0, 12).map((f) => {
    const s = f.surfaces?.length ? `(${f.surfaces.join("")})` : "";
    return `${f.tooth}${s}=${f.label || f.condition}`;
  });
  const more = findings.length > 12 ? ` +${findings.length - 12} more` : "";
  return `${findings.length} finding${findings.length === 1 ? "" : "s"}: ${grouped.join(", ")}${more}.`;
}
